import {useEffect, useRef} from "react";
import {useForm, FormProvider} from "react-hook-form";
import {Alert, Button, Col, message, Row, Space} from "antd";
import {yupResolver} from "@hookform/resolvers/yup";
import {ProductVariantsFormValues} from "../../../../../components/offering-form/products/variants/types";
import {ProductVariantFormFields} from "../../../../../components/offering-form/products/variants";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {useCreateProductContext} from "../../context/create-product";
import {variantFormValidationSchema} from "../../../../../components/offering-form/products/variants/schema";
import {addOnsToFormValues} from "../../../add-ons/add-ons-form";
import {useSaveAddOns} from "../../../add-ons/use-save-add-ons";

export function ProductVariantForm() {
  const hydratedRef = useRef(false);

  const {
    state: {
      apiResponseData,
      variantFormData,
      createdAddOns,
      formData: {currencies, priceType},
    },
    dispatch,
  } = useCreateProductContext();

  const productId = apiResponseData?.product?.id;

  const {saveAddOns, isSaving} = useSaveAddOns(productId ?? 0);

  const variantFormMethods = useForm<ProductVariantsFormValues>({
    resolver: yupResolver(variantFormValidationSchema),
    mode: "onChange",
    defaultValues: variantFormData,
  });

  useEffect(() => {
    if (variantFormData) {
      variantFormMethods.reset(variantFormData, {keepDirty: true});
      hydratedRef.current = true;
    }
  }, [variantFormData, variantFormMethods]);

  const goToAutomations = () =>
    dispatch({type: "update-create-product-form-step", payload: "automations"});

  async function onSubmit(values: ProductVariantsFormValues) {
    // Persist the typed values so navigating back rehydrates them.
    dispatch({type: "save-variant-form-step-data", payload: values});

    // No add-ons wanted → skip the call entirely (matches legacy; empty add-ons
    // are API-rejected and there is nothing to hide).
    if (!values.showVariants) {
      goToAutomations();
      return;
    }

    if (!productId) {
      message.error("Product id not found");
      return;
    }

    try {
      // First pass: all groups are id-less → POST. Re-submit (Back→Next): existing
      // groups carry ids → PUT (no duplicates), newly-added groups → POST.
      // `createdAddOns` is the snapshot used to compute deletions.
      const result = await saveAddOns(createdAddOns, values);

      // Capture the persisted add-ons (with ids) so a return trip upserts.
      dispatch({type: "save-created-add-ons", payload: result});
      dispatch({
        type: "save-variant-form-step-data",
        payload: addOnsToFormValues(result, true),
      });
      goToAutomations();
    } catch (error) {
      const apiError = error as {
        response?: {data?: {message?: string}};
        message?: string;
      };
      message.error(
        apiError.response?.data?.message ||
          apiError.message ||
          "Something went wrong saving your add-ons"
      );
    }
  }

  const isFormError = Object.keys(variantFormMethods.formState.errors).length > 0;
  const isVariantError = variantFormMethods.formState.errors.variants;

  return (
    <>
      <FormProvider {...variantFormMethods}>
        <form onSubmit={variantFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <ProductVariantFormFields
              isVariantError={!!isVariantError}
              productCurrencies={priceType === "free" ? [] : currencies}
              productSelectedPriceType={priceType}
              hydratedRef={hydratedRef}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => {
                      dispatch({
                        type: "save-variant-form-step-data",
                        payload: variantFormMethods.getValues(),
                      });
                      dispatch({
                        type: "update-create-product-form-step",
                        payload: "contentUpload",
                      });
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(variantFormMethods.formState.errors).length > 0 ||
                      Object.values(variantFormMethods.watch()).length < 1
                    }
                    loading={isSaving}
                  >
                    Next
                  </Button>
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </>
  );
}
